import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { flushSync } from 'react-dom';
import { XCircle } from 'lucide-react';
import { inviteApi } from '../api/inviteApi';
import { useAuth } from '@/shared/hooks/useAuth';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { PageHeader } from '@/shared/ui';
import { validatePhone, formatPhoneDisplay, PHONE_PLACEHOLDER } from '@/shared/utils/validation';
import styles from './AcceptInvitePage.module.css';

type Step = 'loading' | 'profile' | 'review' | 'error';

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [step, setStep] = useState<Step>('loading');
  const [profileData, setProfileData] = useState({ name: '', phone: '' });
  const [phoneError, setPhoneError] = useState('');

  const { data: invitation, error: verifyError } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => inviteApi.verifyToken(token!),
    enabled: !!token,
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: () =>
      inviteApi.acceptInvitation(token!, {
        name: profileData.name,
        phone: profileData.phone || undefined,
      }),
    onSuccess: (data) => {
      flushSync(() => {
        login(data.access_token, data.refresh_token, data.user);
      });
      const next = searchParams.get('next');
      const safePath = next && next.startsWith('/') ? next : null;
      navigate(safePath ?? '/', { replace: true });
    },
  });

  useEffect(() => {
    if (invitation) {
      setProfileData((p) => ({
        ...p,
        name: invitation.invitation.contactName ?? '',
        phone: invitation.invitation.contactPhone
          ? formatPhoneDisplay(invitation.invitation.contactPhone)
          : '',
      }));
      setStep('profile');
    } else if (verifyError) {
      setStep('error');
    }
  }, [invitation, verifyError]);

  if (step === 'loading') {
    return (
      <div className={styles.container}>
        <Spinner size="lg" />
        <p className={styles.loadingText}>Verifying your invitation...</p>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.errorIcon}>
            <XCircle size={40} strokeWidth={1.5} aria-hidden />
          </div>
          <h1>Invalid Invitation</h1>
          <p className={styles.errorMessage}>
            {(verifyError as Error)?.message || 'This invitation link is invalid or has expired.'}
          </p>
          <p className={styles.hint}>Please contact your agency for a new invitation.</p>
        </div>
      </div>
    );
  }

  if (step === 'profile') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <PageHeader
            title="Welcome to PracticeNova!"
            subtitle={
              <>
                You've been invited to manage <strong>{invitation?.practice.name}</strong>
              </>
            }
          />

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const err = validatePhone(profileData.phone);
              if (err) {
                setPhoneError(err);
                return;
              }
              setStep('review');
            }}
            className={styles.form}
          >
            <Input
              label="Your Name"
              value={profileData.name}
              onChange={(e) => setProfileData((p) => ({ ...p, name: e.target.value }))}
              placeholder="Dr. John Smith"
              fullWidth
              required
            />
            <Input
              label="Phone"
              type="tel"
              value={profileData.phone}
              onChange={(e) => {
                setProfileData((p) => ({ ...p, phone: e.target.value }));
                setPhoneError('');
              }}
              onBlur={(e) =>
                setProfileData((p) => ({ ...p, phone: formatPhoneDisplay(e.target.value) }))
              }
              placeholder={PHONE_PLACEHOLDER}
              error={phoneError}
              fullWidth
              required
            />
            <Button
              type="submit"
              disabled={!profileData.name.trim() || !profileData.phone.trim()}
              fullWidth
            >
              Continue
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <PageHeader title="Looks good?" subtitle="Review your details before finishing setup." />

          <div className={styles.reviewSection}>
            <h3>Your Profile</h3>
            <div className={styles.reviewGrid}>
              <span className={styles.reviewKey}>Name</span>
              <span className={styles.reviewValue}>{profileData.name}</span>
              <span className={styles.reviewKey}>Email</span>
              <span className={styles.reviewValue}>{invitation?.invitation.email}</span>
              <span className={styles.reviewKey}>Phone</span>
              <span className={styles.reviewValue}>{profileData.phone}</span>
            </div>
          </div>

          <div className={styles.reviewSection}>
            <h3>Practice</h3>
            <div className={styles.reviewGrid}>
              <span className={styles.reviewKey}>Name</span>
              <span className={styles.reviewValue}>{invitation?.practice.name}</span>
              {invitation?.practice.website && (
                <>
                  <span className={styles.reviewKey}>Website</span>
                  <span className={styles.reviewValue}>{invitation.practice.website}</span>
                </>
              )}
            </div>
          </div>

          {acceptMutation.error && (
            <p className={styles.errorMessage}>
              {(acceptMutation.error as Error)?.message || 'Something went wrong'}
            </p>
          )}

          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setStep('profile')}>
              Back
            </Button>
            <Button onClick={() => acceptMutation.mutate()} isLoading={acceptMutation.isPending}>
              Complete Setup
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
